if (!customElements.get('ps-product-studio')) {
  customElements.define('ps-product-studio', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const listen = (element, event, handler) => element?.addEventListener(event, handler, { signal: this.controller.signal });
      this.slides = [...this.querySelectorAll('[data-studio-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-studio-thumb]')];
      this.index = Number(this.dataset.initialIndex) || 0;
      this.dialog = this.querySelector('dialog');
      this.thumbs.forEach((thumb, index) => listen(thumb, 'click', () => this.selectMedia(index)));
      listen(this.querySelector('[data-studio-prev]'), 'click', () => this.selectMedia(this.index - 1));
      listen(this.querySelector('[data-studio-next]'), 'click', () => this.selectMedia(this.index + 1));
      listen(this.querySelector('.ps-studio__gallery'), 'keydown', (event) => {
        if (event.target.matches('input,select,textarea') || event.target.closest('video,model-viewer')) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.selectMedia(this.index + (event.key === 'ArrowRight' ? 1 : -1));
        }
      });
      this.querySelectorAll('[data-studio-zoom]').forEach((button) => listen(button, 'click', () => {
        const source = button.querySelector('img');
        const image = this.dialog.querySelector('img');
        image.src = button.dataset.studioZoomSrc || source.currentSrc || source.src;
        image.alt = source.alt || 'Product preview';
        this.dialog.querySelector('p').textContent = `Preview ${this.index + 1} of ${this.slides.length}`;
        this.dialog.showModal();
      }));
      listen(this.querySelector('[data-studio-close]'), 'click', () => this.dialog.close());
      listen(this.dialog, 'click', (event) => { if (event.target === this.dialog) this.dialog.close(); });

      this.purchaseButton = this.querySelector('product-form button[name="add"]');
      const dock = this.querySelector('[data-studio-dock]');
      if (this.purchaseButton) {
        this.observer = new IntersectionObserver(([entry]) => {
          dock.hidden = entry.isIntersecting || entry.boundingClientRect.top > window.innerHeight;
        }, { threshold: 0.25 });
        this.observer.observe(this.purchaseButton);
        listen(this.querySelector('[data-studio-return]'), 'click', () => {
          this.purchaseButton.scrollIntoView({ behavior: 'auto', block: 'center' });
          this.purchaseButton.focus({ preventScroll: true });
        });
      }

      const select = this.querySelector('[data-studio-variant]');
      if (select) {
        const variants = JSON.parse(this.querySelector('[data-studio-variants]').textContent);
        // The original Shopify form owns submission; keep exactly one variant id field.
        select.removeAttribute('name');
        listen(select, 'change', () => {
          const variant = variants.find((item) => String(item.id) === select.value);
          if (!variant) return;
          const form = this.querySelector('product-form form');
          form.querySelector('input[name="id"]').value = variant.id;
          const money = (amount) => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency, minimumFractionDigits: amount % 100 === 0 ? 0 : 2, maximumFractionDigits: 2 }).format(amount / 100);
          const container = this.querySelector('[id^="price-"] .price');
          container.classList.toggle('price--on-sale', variant.compare_at_price > variant.price);
          container.classList.toggle('price--sold-out', !variant.available);
          container.querySelectorAll('.price-item--sale').forEach((amount) => { amount.textContent = money(variant.price); });
          container.querySelectorAll('.price__regular .price-item--regular').forEach((amount) => { amount.textContent = money(variant.price); });
          container.querySelectorAll('.price__sale .price-item--regular').forEach((amount) => { amount.textContent = money(variant.compare_at_price || variant.price); });
          this.querySelector('[data-studio-dock-price]').textContent = `${money(variant.price)} ${this.dataset.currency}`;
          this.purchaseButton.disabled = !variant.available;
          this.purchaseButton.querySelector('span').textContent = variant.available ? 'Add to cart' : 'Sold out';
          this.purchaseButton.removeAttribute('aria-disabled');
          const url = new URL(location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState({}, '', url);
          if (variant.featured_media) {
            const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(variant.featured_media.id));
            if (index !== -1) this.selectMedia(index);
          }
        });
      }
      this.buildCatalog(listen);
      this.checkBundleDownload();
    }

    async checkBundleDownload() {
      if (!this.dataset.bundleAccessCheck || !this.purchaseButton) return;
      try {
        const response = await fetch(this.dataset.bundleAccessCheck, { credentials: 'same-origin', cache: 'no-store', signal: this.controller.signal });
        if (!response.ok) return;
        const access = await response.json();
        // The URL is a protected storefront endpoint, never a file-provider link.
        if (access.allowed !== true || !/^\/apps\/pixel-sauce-plugin\/web-download\?id=\d+$/.test(access.downloadPath)) return;
        if (!this.isConnected) return;
        const form = this.querySelector('product-form');
        const link = document.createElement('a');
        link.className = 'ps-studio__download button button--full-width button--primary';
        link.href = access.downloadPath;
        link.textContent = 'Download mockups';
        form.before(link);
        form.hidden = true;
        this.querySelectorAll('[data-studio-variant], .ps-studio__variant-label').forEach(element => { element.hidden = true; });
        this.querySelector('.ps-studio__price-row').hidden = true;
        this.querySelector('.ps-studio__delivery span:last-child').textContent = 'Included in your bundle. Download your files instantly.';
        this.querySelector('.ps-studio__upgrade--wide')?.setAttribute('hidden', '');
        const dockButton = this.querySelector('[data-studio-return]');
        const dockLink = link.cloneNode(true);
        dockLink.className = 'ps-studio__dock-download';
        dockButton.replaceWith(dockLink);
        this.querySelector('[data-studio-dock-price]').textContent = 'Included in your bundle';
        this.observer?.unobserve(this.purchaseButton);
        this.observer?.observe(link);
      } catch (_) {
        // Network or verification failure preserves the normal Shopify purchase flow.
      }
    }

    selectMedia(index) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, position) => {
        slide.hidden = position !== this.index;
        if (slide.hidden) {
          slide.querySelectorAll('video').forEach((video) => video.pause());
          slide.querySelectorAll('iframe').forEach((frame) => { const src = frame.src; if (src) frame.src = src; });
        }
      });
      this.thumbs.forEach((thumb, position) => thumb.setAttribute('aria-pressed', String(position === this.index)));
      const thumb = this.thumbs[this.index];
      if (thumb) {
        const container = thumb.parentElement;
        if (thumb.offsetLeft < container.scrollLeft || thumb.offsetLeft + thumb.offsetWidth > container.scrollLeft + container.clientWidth) {
          container.scrollTo({ left: thumb.offsetLeft - container.offsetLeft - container.clientWidth / 2 + thumb.offsetWidth / 2, behavior: 'auto' });
        }
      }
      const counter = this.querySelector('[data-studio-counter]');
      if (counter) counter.textContent = `${this.index + 1} / ${this.slides.length}`;
    }

    buildCatalog(listen) {
      const target = this.querySelector('[data-studio-catalog]');
      if (!target) return;
      const normalize = (value) => value.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
      const groups = [...target.querySelectorAll('[data-studio-group]')].map((element) => {
        const name = element.querySelector('[data-studio-brand]').textContent;
        return {
          element,
          rows: [...element.querySelectorAll('li')].map((row) => ({ element: row, search: normalize(`${name} ${row.textContent}`) }))
        };
      });
      if (!groups.length) return;
      const count = this.querySelector('[data-studio-search-count]');
      const search = this.querySelector('[data-studio-search]');
      this.querySelector('[data-studio-search-wrap]').hidden = false;
      const update = () => {
        const terms = normalize(search.value).split(' ').filter(Boolean);
        let matches = 0;
        let brands = 0;
        groups.forEach((group) => {
          let visible = 0;
          group.rows.forEach((row) => {
            row.element.hidden = !terms.every((term) => row.search.includes(term));
            if (!row.element.hidden) visible++;
          });
          group.element.hidden = visible === 0;
          group.element.open = terms.length > 0 && visible > 0;
          group.element.querySelector('small').textContent = `${visible} collection${visible === 1 ? '' : 's'}`;
          matches += visible;
          if (visible) brands++;
        });
        count.textContent = matches ? `${matches} product collection${matches === 1 ? '' : 's'} across ${brands} brand group${brands === 1 ? '' : 's'}. Each collection can contain multiple mockup files.` : 'No matching collections. Try another brand or style number.';
      };
      listen(search, 'input', update);
      listen(this.querySelector('[data-studio-clear]'), 'click', () => { search.value = ''; update(); search.focus(); });
      update();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
    }
  });
}
